import React from 'react'
import { IconButton, Popover, Box, Typography, Tooltip } from '@mui/material'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import CheckIcon from '@mui/icons-material/Check'
import { useThemePreference } from '@theme/ThemePreferenceContext'
import { THEMES_BY_ID, THEME_PRESET_GRID, type ThemePresetId } from '@theme/themePresets'

const THEME_LABEL = 'Chủ đề'

const SWATCH_BOX = {
  width: 44,
  height: 44,
  borderRadius: '10px',
  cursor: 'pointer',
  border: '2px solid',
  transition: 'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 16px rgba(15, 23, 42, 0.12)',
  },
} as const

const PRESET_ROWS = THEME_PRESET_GRID.flat()

type ThemePickerPopoverProps = {
  /** `header`: nút trắng trên navbar tối. `surface`: nút trên nền sáng (login…). */
  variant?: 'header' | 'surface'
}

const ThemePickerPopover: React.FC<ThemePickerPopoverProps> = ({ variant = 'header' }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const { presetId, setPresetId, palette } = useThemePreference()
  const open = Boolean(anchorEl)
  const isSurface = variant === 'surface'

  return (
    <>
      <Tooltip title={THEME_LABEL}>
        <IconButton
          type="button"
          size="medium"
          color="inherit"
          aria-label={THEME_LABEL}
          aria-haspopup="true"
          aria-expanded={open}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={
            isSurface
              ? {
                  color: palette.primary.main,
                  border: `1px solid ${palette.primary.surfaceBorder}`,
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
                  '&:hover': {
                    backgroundColor: palette.primary.surface,
                  },
                }
              : {
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.18)',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.14)',
                    borderColor: 'rgba(255, 255, 255, 0.55)',
                  },
                }
          }
        >
          <PaletteOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1.75,
            borderRadius: 2,
            minWidth: 200,
            boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12)',
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25, color: 'text.primary' }}>
          {THEME_LABEL}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 44px)',
            gap: '10px',
            justifyContent: 'center',
          }}
          role="listbox"
          aria-label={THEME_LABEL}
        >
          {PRESET_ROWS.map((id) => {
            const swatchMain = THEMES_BY_ID[id].primary.main
            const selected = id === presetId
            return (
              <Box
                key={id}
                component="button"
                type="button"
                role="option"
                aria-selected={selected}
                title={id}
                onClick={() => {
                  setPresetId(id as ThemePresetId)
                  setAnchorEl(null)
                }}
                sx={{
                  ...SWATCH_BOX,
                  bgcolor: swatchMain,
                  borderColor: selected ? '#ffffff' : 'rgba(15,23,42,0.12)',
                  boxShadow: selected ? '0 0 0 2px rgba(255,255,255,0.95), 0 0 0 4px rgba(15,23,42,0.15)' : 'none',
                }}
              >
                {selected ? <CheckIcon sx={{ color: '#fff', fontSize: 22 }} /> : null}
              </Box>
            )
          })}
        </Box>
      </Popover>
    </>
  )
}

export default ThemePickerPopover
